import { useQueryParameters } from '../../../common/hooks/QueryParamsHook'
import {
  SearchDeliveryNotesForExportDocument,
  useSearchDeliveryNotesWithCustomerDetailsLazyQuery,
  useSearchDeliveryNotesQuery,
} from '../../../generated/graphql'
import { useEffect, useMemo } from 'react'
import { ordersTableColumns } from './OrdersTableColumns'
import { ApolloResult } from '../../../common/ApolloResult'
import { Snack, Tooltip } from '@nike/eds'
import { PaginatedTable } from '../../../common/components/table/PaginatedTable'
import { ExportQuery } from '../../../common/components/export/ExportService'
import { ExportButton } from '../../../common/components/export/ExportButton'
import { ordersXlsxColumns } from './OrdersXlsxColumns'
import useRoleContext from '../../../common/hooks/useRoleContext'

export function OrdersTable() {
  const [{ paging, searchParameters }] = useQueryParameters()
  const hasCustomerDetailsAccess = useRoleContext().hasCustomerDetailsAccess()

  const queryResult = useSearchDeliveryNotesQuery({
    variables: {
      ...searchParameters,
      search: searchParameters.search?.join(' '),
      from: paging.from,
      size: paging.size,
    },
    fetchPolicy: 'no-cache',
    context: {
      page: 'orders',
      debounceKey: 'ordersSearch',
    },
  })

  const [fetchDeliveryNotesWithCustomerDetails, queryResultWithCustomerDetails] =
    useSearchDeliveryNotesWithCustomerDetailsLazyQuery({
      variables: {
        ...searchParameters,
        search: searchParameters.search?.join(' '),
        from: paging.from,
        size: paging.size,
      },
      fetchPolicy: 'no-cache',
      context: {
        page: 'orders',
        debounceKey: 'ordersSearch',
      },
    })

  useEffect(() => {
    if (hasCustomerDetailsAccess) {
      fetchDeliveryNotesWithCustomerDetails()
    }
  }, [hasCustomerDetailsAccess, fetchDeliveryNotesWithCustomerDetails])

  const orders = hasCustomerDetailsAccess
    ? queryResultWithCustomerDetails.data?.searchDeliveryNotes
    : queryResult.data?.searchDeliveryNotes
  const columns = useMemo(() => ordersTableColumns, [])

  const exportQuery: ExportQuery = {
    query: SearchDeliveryNotesForExportDocument,
    variables: { ...searchParameters, search: searchParameters.search?.join(' ') },
    name: 'searchDeliveryNotes',
    action: 'exportDeliveryNotes',
    context: {
      page: 'orders',
    },
  }

  return (
    <ApolloResult {...queryResult}>
      <div className="table-title-row">
        <Tooltip bodySlot="Export by Delivery Note">
          <ExportButton
            xlsxColumns={ordersXlsxColumns}
            pageSize={100}
            exportQuery={exportQuery}
            className="table-export-button"
            fileName="orders.xlsx"
          />
        </Tooltip>
      </div>
      {orders?.length > 0 ? (
        <PaginatedTable columns={columns} data={orders} paging={paging} className="orders-table" />
      ) : (
        <Snack id="no-cartons-snack" onDismiss={() => {}} hideDismiss={true} status="info">
          No orders found
        </Snack>
      )}
    </ApolloResult>
  )
}
